import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  AlertTriangle, Search, Filter, Download, Eye, 
  Lock, Unlock, CreditCard, UserCheck, Settings,
  Shield, Key, Database, FileText, Clock, Cookie
} from "lucide-react";

interface SensitiveAction {
  id: string;
  action: string;
  description: string;
  riskLevel: 'critical' | 'high' | 'medium' | 'low';
  category: 'authentication' | 'payment' | 'data-access' | 'configuration' | 'security';
  domain: string;
  detectedAt: string;
  userInteraction: boolean;
  encrypted: boolean;
  complianceIssue: boolean;
  cookieDetails?: {
    name: string;
    domain: string;
    httpOnly: boolean;
    secure: boolean;
    sameSite: string;
    path?: string;
    hstsEnabled?: boolean;
  };
  storageDetails?: {
    type: 'localStorage' | 'sessionStorage';
    sensitiveKeys: string[];
    totalKeys: number;
    allKeys?: string[];
  };
}

export default function SensitiveActions() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [selectedAction, setSelectedAction] = useState<SensitiveAction | null>(null);
  const [activeCardFilter, setActiveCardFilter] = useState<string | null>(null); // 'total' | 'critical' | 'high' | 'medium' | 'low' | null

  // Fetch sensitive actions data from API
  const { data: actionsData, isLoading } = useQuery<{
    actions: SensitiveAction[];
    summary: {
      total: number;
      critical: number;
      high: number;
      medium: number;
      low: number;
      complianceIssues: number;
    };
  }>({
    queryKey: ["/api/sensitive-actions"],
    refetchInterval: 60000, // Refresh every minute
  });

  const actions = actionsData?.actions || [];
  const summary = actionsData?.summary || {
    total: 0,
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    complianceIssues: 0
  };

  const handleCardClick = (filterType: 'total' | 'critical' | 'high' | 'medium' | 'low') => {
    if (activeCardFilter === filterType) {
      setActiveCardFilter(null);
    } else {
      setActiveCardFilter(filterType);
    }
  };

  const filteredActions = actions.filter(action => {
    const matchesSearch = 
      action.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      action.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      action.domain.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterCategory === "all" || action.category === filterCategory;
    
    // Apply card filter
    let matchesCardFilter = true;
    if (activeCardFilter === 'critical') {
      matchesCardFilter = action.riskLevel === 'critical';
    } else if (activeCardFilter === 'high') {
      matchesCardFilter = action.riskLevel === 'high';
    } else if (activeCardFilter === 'medium') {
      matchesCardFilter = action.riskLevel === 'medium';
    } else if (activeCardFilter === 'low') {
      matchesCardFilter = action.riskLevel === 'low';
    }
    // 'total' or null means show all
    
    return matchesSearch && matchesFilter && matchesCardFilter;
  });

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'authentication': return <UserCheck className="h-4 w-4 text-blue-500" />;
      case 'payment': return <CreditCard className="h-4 w-4 text-purple-500" />;
      case 'data-access': return <Database className="h-4 w-4 text-purple-500" />;
      case 'configuration': return <Settings className="h-4 w-4 text-orange-500" />;
      case 'security': return <Shield className="h-4 w-4 text-red-500" />;
      default: return <AlertTriangle className="h-4 w-4 text-gray-500" />;
    }
  };

  const hasCookieDetails = (action: SensitiveAction) => {
    return action.cookieDetails !== undefined && action.cookieDetails !== null;
  };
  const hasStorageDetails = (action: SensitiveAction) => {
    return action.storageDetails !== undefined && action.storageDetails !== null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Sensitive Actions
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Monitor and analyze sensitive user interactions and security-critical operations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              const link = document.createElement('a');
              link.href = '/api/export/sensitive-actions';
              link.download = `sensitive-actions-${new Date().toISOString().split('T')[0]}.csv`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
          <Badge variant="secondary" className="text-xs">
            <AlertTriangle className="h-3 w-3 mr-1" />
            {summary.complianceIssues} Compliance Issues
          </Badge>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeCardFilter === 'total' ? 'ring-2 ring-slate-500 bg-slate-50 dark:bg-slate-950' : ''
          }`}
          onClick={() => handleCardClick('total')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                activeCardFilter === 'total' ? 'bg-slate-200' : 'bg-slate-100'
              }`}>
                <AlertTriangle className={`h-5 w-5 ${
                  activeCardFilter === 'total' ? 'text-slate-700' : 'text-slate-600'
                }`} />
              </div>
              <div>
                <div className="text-xl font-bold text-slate-900 dark:text-white">
                  {summary.total}
                </div>
                <div className="text-xs text-slate-600">Total Actions</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeCardFilter === 'critical' ? 'ring-2 ring-red-500 bg-red-50 dark:bg-red-950' : ''
          }`}
          onClick={() => handleCardClick('critical')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                activeCardFilter === 'critical' ? 'bg-red-200' : 'bg-red-100'
              }`}>
                <AlertTriangle className={`h-5 w-5 ${
                  activeCardFilter === 'critical' ? 'text-red-700' : 'text-red-600'
                }`} />
              </div>
              <div>
                <div className={`text-xl font-bold ${
                  activeCardFilter === 'critical' ? 'text-red-700' : 'text-red-600'
                }`}>
                  {summary.critical}
                </div>
                <div className="text-xs text-slate-600">Critical</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeCardFilter === 'high' ? 'ring-2 ring-orange-500 bg-orange-50 dark:bg-orange-950' : ''
          }`}
          onClick={() => handleCardClick('high')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                activeCardFilter === 'high' ? 'bg-orange-200' : 'bg-orange-100'
              }`}>
                <AlertTriangle className={`h-5 w-5 ${
                  activeCardFilter === 'high' ? 'text-orange-700' : 'text-orange-600'
                }`} />
              </div>
              <div>
                <div className={`text-xl font-bold ${
                  activeCardFilter === 'high' ? 'text-orange-700' : 'text-orange-600'
                }`}>
                  {summary.high}
                </div>
                <div className="text-xs text-slate-600">High Risk</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeCardFilter === 'medium' ? 'ring-2 ring-yellow-500 bg-yellow-50 dark:bg-yellow-950' : ''
          }`}
          onClick={() => handleCardClick('medium')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                activeCardFilter === 'medium' ? 'bg-yellow-200' : 'bg-yellow-100'
              }`}>
                <AlertTriangle className={`h-5 w-5 ${
                  activeCardFilter === 'medium' ? 'text-yellow-700' : 'text-yellow-600'
                }`} />
              </div>
              <div>
                <div className={`text-xl font-bold ${
                  activeCardFilter === 'medium' ? 'text-yellow-700' : 'text-yellow-600'
                }`}>
                  {summary.medium}
                </div>
                <div className="text-xs text-slate-600">Medium Risk</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeCardFilter === 'low' ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-950' : ''
          }`}
          onClick={() => handleCardClick('low')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                activeCardFilter === 'low' ? 'bg-blue-200' : 'bg-blue-100'
              }`}>
                <Shield className={`h-5 w-5 ${
                  activeCardFilter === 'low' ? 'text-blue-700' : 'text-blue-600'
                }`} />
              </div>
              <div>
                <div className={`text-xl font-bold ${
                  activeCardFilter === 'low' ? 'text-blue-700' : 'text-blue-600'
                }`}>
                  {summary.complianceIssues}
                </div>
                <div className="text-xs text-slate-600">PCI Issues</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search sensitive actions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-500" />
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">All Categories</option>
            <option value="authentication">Authentication</option>
            <option value="payment">Payment</option>
            <option value="data-access">Data Access</option>
            <option value="configuration">Configuration</option>
            <option value="security">Security</option>
          </select>
        </div>
      </div>

      {/* Sensitive Actions List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            Detected Sensitive Actions ({filteredActions.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-slate-600">Loading sensitive actions...</span>
            </div>
          ) : filteredActions.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Shield className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <div className="text-lg font-medium mb-2">No Sensitive Actions Found</div>
              <div className="text-sm">
                {searchTerm || filterCategory !== "all" 
                  ? "Try adjusting your search or filter criteria."
                  : "No sensitive actions have been detected in your recent scans."}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredActions.map((action) => (
                <div
                  key={action.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedAction(action)}
                >
                  <div className="flex items-center gap-4 flex-1">
                    {getCategoryIcon(action.category)}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{action.action}</span>
                        <Badge className={`text-xs border ${getRiskColor(action.riskLevel)}`}>
                          {action.riskLevel}
                        </Badge>
                        {action.complianceIssue && (
                          <Badge variant="destructive" className="text-xs">
                            PCI
                          </Badge>
                        )}
                        {action.encrypted && (
                          <Badge variant="secondary" className="text-xs">
                            <Lock className="h-3 w-3 mr-1" />
                            Encrypted
                          </Badge>
                        )}
                        {hasCookieDetails(action) && (
                          <Badge variant="outline" className="text-xs">
                            <Cookie className="h-3 w-3 mr-1" />
                            Cookie
                          </Badge>
                        )}
                        {hasStorageDetails(action) && (
                          <Badge variant="outline" className="text-xs">
                            <Database className="h-3 w-3 mr-1" />
                            Storage
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                        {action.description}
                      </div>
                      {/* Cookie Quick Info */}
                      {hasCookieDetails(action) && (
                        <div className="flex items-center gap-3 text-xs mt-2">
                          <div className="flex items-center gap-1">
                            <span className="text-slate-500">HttpOnly:</span>
                            {action.cookieDetails?.httpOnly ? (
                              <Badge className="bg-green-100 text-green-800 text-xs">Yes</Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-800 text-xs">No</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-slate-500">Secure:</span>
                            {action.cookieDetails?.secure ? (
                              <Badge className="bg-green-100 text-green-800 text-xs">Yes</Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-800 text-xs">No</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-slate-500">SameSite:</span>
                            {action.cookieDetails?.sameSite === 'Strict' || action.cookieDetails?.sameSite === 'Lax' ? (
                              <Badge className="bg-green-100 text-green-800 text-xs">{action.cookieDetails.sameSite}</Badge>
                            ) : action.cookieDetails?.sameSite === 'None' ? (
                              <Badge className="bg-orange-100 text-orange-800 text-xs">None</Badge>
                            ) : (
                              <Badge className="bg-yellow-100 text-yellow-800 text-xs">{action.cookieDetails?.sameSite || 'Unset'}</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-slate-500">HSTS:</span>
                            {action.cookieDetails?.hstsEnabled ? (
                              <Badge className="bg-green-100 text-green-800 text-xs">Enabled</Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-800 text-xs">Missing</Badge>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          {action.domain}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(action.detectedAt).toLocaleString()}
                        </span>
                        <span className="capitalize">{action.category}</span>
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm">
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Details Modal */}
      {selectedAction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {getCategoryIcon(selectedAction.category)}
                Sensitive Action Details
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedAction(null)}
                  className="ml-auto"
                >
                  ×
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-600">Action</label>
                  <div className="font-mono text-sm bg-slate-100 p-2 rounded">
                    {selectedAction.action}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Risk Level</label>
                  <div>
                    <Badge className={`${getRiskColor(selectedAction.riskLevel)}`}>
                      {selectedAction.riskLevel.toUpperCase()}
                    </Badge>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Category</label>
                  <div className="flex items-center gap-2">
                    {getCategoryIcon(selectedAction.category)}
                    <span className="capitalize">{selectedAction.category}</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Domain</label>
                  <div className="text-sm">{selectedAction.domain}</div>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-600">Description</label>
                <div className="text-sm bg-slate-50 p-3 rounded border">
                  {selectedAction.description}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-600">User Interaction</label>
                  <div className="flex items-center gap-2">
                    {selectedAction.userInteraction ? (
                      <UserCheck className="h-4 w-4 text-purple-500" />
                    ) : (
                      <Settings className="h-4 w-4 text-gray-500" />
                    )}
                    <span className="text-sm">
                      {selectedAction.userInteraction ? 'User-initiated' : 'Automated'}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Encryption Status</label>
                  <div className="flex items-center gap-2">
                    {selectedAction.encrypted ? (
                      <Lock className="h-4 w-4 text-purple-500" />
                    ) : (
                      <Unlock className="h-4 w-4 text-red-500" />
                    )}
                    <span className="text-sm">
                      {selectedAction.encrypted ? 'Encrypted' : 'Unencrypted'}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-600">Detected At</label>
                <div className="text-sm">{new Date(selectedAction.detectedAt).toLocaleString()}</div>
              </div>

              {selectedAction.complianceIssue && (
                <div className="bg-red-50 border border-red-200 p-3 rounded">
                  <div className="flex items-center gap-2 text-red-800">
                    <AlertTriangle className="h-4 w-4" />
                    <span className="font-medium">PCI Compliance Issue</span>
                  </div>
                  <div className="text-sm text-red-700 mt-1">
                    This action may violate PCI DSS requirements and requires immediate attention.
                  </div>
                </div>
              )}

              {/* Cookie Details */}
              {selectedAction.cookieDetails && (
                <div className="bg-blue-50 border border-blue-200 p-4 rounded">
                  <div className="flex items-center gap-2 text-blue-800 mb-3">
                    <Cookie className="h-4 w-4" />
                    <span className="font-medium">Cookie Security Details</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="font-medium text-slate-600">Cookie Name:</span>
                      <div className="font-mono text-xs bg-white p-2 rounded mt-1">
                        {selectedAction.cookieDetails.name}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-slate-600">Domain:</span>
                      <div className="text-xs mt-1">{selectedAction.cookieDetails.domain}</div>
                    </div>
                    <div>
                      <span className="font-medium text-slate-600">HttpOnly:</span>
                      <div className="mt-1">
                        {selectedAction.cookieDetails.httpOnly ? (
                          <Badge className="bg-green-100 text-green-800">Yes</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800">No ⚠️</Badge>
                        )}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-slate-600">Secure:</span>
                      <div className="mt-1">
                        {selectedAction.cookieDetails.secure ? (
                          <Badge className="bg-green-100 text-green-800">Yes</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800">No ⚠️</Badge>
                        )}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-slate-600">SameSite:</span>
                      <div className="mt-1">
                        {selectedAction.cookieDetails.sameSite === 'Strict' ? (
                          <Badge className="bg-green-100 text-green-800">Strict ✅</Badge>
                        ) : selectedAction.cookieDetails.sameSite === 'Lax' ? (
                          <Badge className="bg-green-100 text-green-800">Lax ✅</Badge>
                        ) : selectedAction.cookieDetails.sameSite === 'None' ? (
                          <Badge className="bg-orange-100 text-orange-800">None ⚠️ (Requires Secure flag)</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800">{selectedAction.cookieDetails.sameSite || 'Unset'} ⚠️ (CSRF Risk)</Badge>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {selectedAction.cookieDetails.sameSite === 'Strict' && 'Best protection against CSRF attacks'}
                        {selectedAction.cookieDetails.sameSite === 'Lax' && 'Good protection, allows top-level navigation'}
                        {selectedAction.cookieDetails.sameSite === 'None' && 'No CSRF protection - requires Secure flag'}
                        {!selectedAction.cookieDetails.sameSite || selectedAction.cookieDetails.sameSite === 'Unset' && 'No SameSite attribute - vulnerable to CSRF'}
                      </div>
                    </div>
                    {selectedAction.cookieDetails.path && (
                      <div>
                        <span className="font-medium text-slate-600">Path:</span>
                        <div className="text-xs mt-1 font-mono">{selectedAction.cookieDetails.path}</div>
                      </div>
                    )}
                    <div>
                      <span className="font-medium text-slate-600">HSTS Header:</span>
                      <div className="mt-1">
                        {selectedAction.cookieDetails.hstsEnabled ? (
                          <Badge className="bg-green-100 text-green-800">Enabled</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800">Missing ⚠️</Badge>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {selectedAction.cookieDetails.hstsEnabled 
                          ? 'HSTS header is present - cookies are protected during transport'
                          : 'HSTS header is missing - cookies may be vulnerable to man-in-the-middle attacks'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Storage Details */}
              {selectedAction.storageDetails && (
                <div className="bg-purple-50 border border-purple-200 p-4 rounded">
                  <div className="flex items-center gap-2 text-purple-800 mb-3">
                    <Database className="h-4 w-4" />
                    <span className="font-medium">Browser Storage Details</span>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="font-medium text-slate-600">Storage Type:</span>
                      <div className="mt-1">
                        <Badge className="bg-purple-100 text-purple-800">
                          {selectedAction.storageDetails.type}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-slate-600">Total Keys:</span>
                      <div className="text-xs mt-1">{selectedAction.storageDetails.totalKeys} items stored</div>
                    </div>
                    {selectedAction.storageDetails.sensitiveKeys.length > 0 && (
                      <div>
                        <span className="font-medium text-slate-600">Sensitive Keys Detected:</span>
                        <div className="mt-2 space-y-1">
                          {selectedAction.storageDetails.sensitiveKeys.map((key, idx) => (
                            <div key={idx} className="font-mono text-xs bg-white p-2 rounded border border-red-200">
                              <AlertTriangle className="h-3 w-3 inline text-red-600 mr-1" />
                              {key}
                            </div>
                          ))}
                        </div>
                        <div className="text-xs text-red-700 mt-2">
                          ⚠️ Sensitive data stored in browser storage is accessible to JavaScript and may be vulnerable to XSS attacks.
                        </div>
                      </div>
                    )}
                    {selectedAction.storageDetails.allKeys && selectedAction.storageDetails.allKeys.length > 0 && (
                      <div>
                        <span className="font-medium text-slate-600">All Storage Keys:</span>
                        <div className="mt-2 space-y-1">
                          {selectedAction.storageDetails.allKeys.map((key, idx) => (
                            <div key={idx} className="font-mono text-xs bg-white p-2 rounded border border-slate-200">
                              {key}
                            </div>
                          ))}
                          {selectedAction.storageDetails.totalKeys > selectedAction.storageDetails.allKeys.length && (
                            <div className="text-xs text-slate-500 mt-1">
                              ... and {selectedAction.storageDetails.totalKeys - selectedAction.storageDetails.allKeys.length} more
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}